import { Panel } from 'primereact/panel';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PanelContainer, ReadMoreInlineButton } from 'styles/v2/Styles.styled';
import ToggleButton from './panels/toggleable/ToggleButton';
import { STATUS_COMMENT_MAX_LENGTH } from 'constants/constants';
import { useState } from 'react';
import StatusCommentFullViewDialog from '../dialogs/StatusCommentFullViewDialog';

type TProps = {
  statusDisplayName?: string;
  statusMessageCode?: string;
  commentToStatus?: string | null;
  preserveCommentLineBreaks?: boolean;
};

const StatusBox = ({ statusDisplayName, statusMessageCode, commentToStatus, preserveCommentLineBreaks }: TProps) => {
  const [showFullCommentDialog, setShowFullCommentDialog] = useState(false);
  const { t } = useTranslation();

  const isEPDDepublishedByAdminOrDeclined =
    statusMessageCode === 'declinedVerification' ||
    statusMessageCode === 'depublishedEPDNotPaid' ||
    statusMessageCode === 'depublishedEPDNonCompliant' ||
    statusMessageCode === 'declinedPublication' ||
    statusMessageCode === 'declinedPublicationProcessCertification';

  const commentToStatusPostProcessed =
    commentToStatus &&
    (commentToStatus.length > STATUS_COMMENT_MAX_LENGTH
      ? commentToStatus.slice(0, STATUS_COMMENT_MAX_LENGTH) + '…'
      : commentToStatus);

  const headerTemplate = (options: any) => {
    const className = `${options.className} justify-content-space-between w-full`;

    return (
      <div
        className={className}
        style={{
          borderRadius: options.collapsed ? '4px' : undefined,
          paddingRight: 0,
          height: '3rem',
          marginBottom: options.collapsed ? undefined : '-0.5rem',
        }}
      >
        <StatusLabel style={options.collapsed ? { whiteSpace: 'nowrap' } : {}}>
          EPD status: {statusDisplayName ? t(`epdWizard.epdState.status.${statusDisplayName}`) : ''}
        </StatusLabel>
        <ToggleButton options={options} withMargins />
      </div>
    );
  };

  return (
    <PanelContainer style={{ boxShadow: 'none' }}>
      <StatusBoxPanel
        headerTemplate={(options) => headerTemplate(options)}
        $isEPDDepublishedByAdminOrDeclined={isEPDDepublishedByAdminOrDeclined}
        toggleable
        collapsed
      >
        {!!statusDisplayName && !!statusMessageCode && (
          <StatusMessagesWrapper>
            <p>{t(`epdWizard.epdState.statusMessage.${statusMessageCode}`)}</p>
            <div>
              <ActionLabel>{t('epdWizard.epdState.action.title')}</ActionLabel>
              <p style={{ marginTop: 8 }}>{t(`epdWizard.epdState.actionMessage.${statusMessageCode}`)}</p>
            </div>
            {isEPDDepublishedByAdminOrDeclined && commentToStatus && (
              <div>
                <ActionLabel>
                  {statusMessageCode === 'declinedVerification'
                    ? t('epdWizard.epdState.action.commentByVerifierTitle')
                    : t('epdWizard.epdState.action.commentBySecretariatTitle')}
                </ActionLabel>
                <p style={{ marginTop: 8 }}>
                  {commentToStatusPostProcessed}{' '}
                  {commentToStatusPostProcessed!.length > STATUS_COMMENT_MAX_LENGTH && (
                    <ReadMoreInlineButton onClick={() => setShowFullCommentDialog(true)}>Read more</ReadMoreInlineButton>
                  )}
                </p>
              </div>
            )}
          </StatusMessagesWrapper>
        )}
      </StatusBoxPanel>
      <StatusCommentFullViewDialog
        headerTitle={
          statusMessageCode === 'declinedVerification'
            ? t('epdWizard.epdState.action.commentByVerifierTitle')
            : t('epdWizard.epdState.action.commentBySecretariatTitle')
        }
        comment={commentToStatus!}
        isOpened={showFullCommentDialog}
        onClose={() => setShowFullCommentDialog(false)}
        preserveLineBreaks={preserveCommentLineBreaks}
      />
    </PanelContainer>
  );
};

const StatusMessagesWrapper = styled.div`
  display: grid;
  gap: 1rem;
`;

const StatusLabel = styled.label`
  flex-wrap: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 185px;
  padding: 8px 0;
`;

const ActionLabel = styled(StatusLabel)`
  margin-bottom: 1rem;
`;

const StatusBoxPanel = styled(Panel)<{ $isEPDDepublishedByAdminOrDeclined?: boolean }>`
  background-color: ${(props) =>
    props.$isEPDDepublishedByAdminOrDeclined ? props.theme.colors.statusLabelRedBg : props.theme.colors.statusLabelGreenBg};
  border-radius: 4px;
  border: 0;
  color: ${(props) => props.theme.colors.primaryBlack70};
  ${StatusLabel} {
    ${(props) => props.theme.typography.headerCaptionSemiBold};
    color: ${(props) =>
      props.$isEPDDepublishedByAdminOrDeclined ? props.theme.colors.statusLabelRedText : props.theme.colors.primaryGreen};
  }

  .p-panel-header {
    border: 0;
    background-color: transparent;
  }
  .p-panel-content {
    border: 0;
    padding-top: 0;
    background-color: transparent;
    ${(props) => props.theme.typography.contentInput};
    color: ${(props) => props.theme.colors.primaryBlack70};
  }

  p {
    margin-top: 4px;
    margin-bottom: 0;
  }
`;

export default StatusBox;
